import { useEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react'
import { useLocation } from 'react-router'

import { useAssistant } from '../../contexts/AssistantContext'
import { useKeyboardInset } from '../../lib/keyboardInset'
import { AssistantMark } from './AssistantMark'
import { AssistantPanel, focusOpening } from './AssistantPanel'

/**
 * The bubble and the drawer (agent-1-web W2), mounted by `AppShell` as the
 * shell's last child when the build has the assistant.
 *
 * - **The bubble** -- a real `<button>`, 56 px, bottom-right -- appears on
 *   product screens only: never on `/admin/*`, never on `/assistant` (the page
 *   IS the assistant), and the signed-out screens have no shell at all. It is
 *   hidden, not unmounted, while the drawer is open, so focus can come back to
 *   the very element that opened it.
 * - **From 1024 px** the drawer is an `<aside aria-label="Assistant">` in the
 *   shell's third grid track (`.shell:has(> .assistant-drawer)`): it PUSHES the
 *   page, as the canvas draws it, rather than covering it. It is not modal, so
 *   Escape is handled here.
 * - **On a phone** it is a `<dialog>` sheet opened with `showModal()`, which
 *   gives it the platform's focus trap, Escape and inert background -- the
 *   discipline `AppShell`'s navigation drawer follows, including closing it
 *   explicitly when it unmounts (a removed `<dialog>` fires no `close`).
 *
 * The conversation lives in the provider, so crossing the breakpoint with the
 * drawer open moves it between the two containers instead of losing it.
 */
export function AssistantDock({ isDesktop }: { isDesktop: boolean }) {
  // agent-1.1 S9.2: the phone sheet rides above an on-screen keyboard.
  const keyboard = useKeyboardInset()
  const assistant = useAssistant()
  const { pathname } = useLocation()
  const bubbleRef = useRef<HTMLButtonElement>(null)
  const sheetRef = useRef<HTMLDialogElement>(null)
  const open = assistant?.open ?? false
  const route = assistant?.screen?.route ?? null
  const shown = route !== null && route !== 'assistant'
  const close = assistant?.closeAssistant

  // `/assistant` is the assistant; the drawer gives way to it.
  useEffect(() => {
    if (open && !shown) close?.()
  }, [open, shown, close])

  // On a phone the sheet covers the page, so a link followed from it closes it:
  // the person went there to see it.
  const lastPath = useRef(pathname)
  useEffect(() => {
    if (lastPath.current === pathname) return
    lastPath.current = pathname
    if (open && !isDesktop) close?.()
  }, [pathname, open, isDesktop, close])

  // Focus goes back to whatever opened the drawer: "Ask about this", or the bubble.
  const wasOpen = useRef(open)
  const triggerRef = assistant?.triggerRef
  useEffect(() => {
    if (wasOpen.current && !open) {
      const trigger = triggerRef?.current
      const target = trigger?.isConnected ? trigger : (bubbleRef.current ?? document.getElementById('main'))
      target?.focus()
    }
    wasOpen.current = open
  }, [open, triggerRef])

  // The phone sheet, driven from state as AppShell's drawer is.
  const sheetOpen = open && shown && !isDesktop
  useEffect(() => {
    const dialog = sheetRef.current
    if (!dialog) return
    if (sheetOpen && !dialog.open) {
      dialog.showModal()
      // showModal() moves focus to the dialog's first focusable control, AFTER
      // the panel's own focus effect ran: so the composer is focused here
      // (W2/W3 review, measured in chromium and webkit at 390px).
      focusOpening(dialog)
    } else if (!sheetOpen && dialog.open) dialog.close()
    return () => {
      if (dialog.open) dialog.close()
    }
  }, [sheetOpen])

  if (!assistant || !shown) return null

  const onAsideKey = (event: KeyboardEvent) => {
    // An Escape inside a dialog in the drawer ("How it works") is that
    // dialog's: it closes the dialog, not the drawer (W2/W3 review).
    if ((event.target as Element).closest('dialog')) return
    if (event.key === 'Escape') {
      event.stopPropagation()
      assistant.closeAssistant()
    }
  }

  return (
    <>
      <button
        ref={bubbleRef}
        type="button"
        className="assistant-bubble"
        aria-label="Open the assistant"
        aria-haspopup={isDesktop ? undefined : 'dialog'}
        hidden={open}
        onClick={(e) => assistant.openAssistant({ trigger: e.currentTarget })}
      >
        <AssistantMark />
      </button>

      {isDesktop && open && (
        <aside className="assistant-drawer" aria-label="Assistant" onKeyDown={onAsideKey}>
          <AssistantPanel variant="drawer" />
        </aside>
      )}

      {!isDesktop && (
        <dialog
          ref={sheetRef}
          className="assistant-sheet"
          aria-label="Assistant"
          onClose={assistant.closeAssistant}
          style={{ '--assistant-keyboard-inset': `${keyboard}px` } as CSSProperties}
        >
          {sheetOpen && <AssistantPanel variant="sheet" />}
        </dialog>
      )}
    </>
  )
}
